/**
 * Bring whatever the server hands back to the shape the designer edits.
 *
 * The server normalises too — `er_model.normalise` is the authority and always
 * will be. This exists because the designer must not CRASH on a model that is
 * missing a block, and it met exactly that: a backend still serving the
 * pre-revision-2 normaliser answers with an `anchor` and no `open`, and the
 * first click on "Only one open document per record" threw
 * `Cannot set properties of undefined`.
 *
 * A designer that blows up on an unexpected payload is the worst place for that
 * failure, because it is the screen somebody would fix the payload on. So every
 * block the editor writes into is guaranteed to exist here, and a legacy
 * `anchor` is rewritten into `keys` + `transactions` + `open` the same way the
 * server does it — which means an older form opens, edits, and SAVES in the
 * current shape rather than being stuck.
 *
 * Deliberately NOT a second normaliser: it fills in and upgrades, and touches
 * nothing it already understands. Everything about what a value MEANS stays on
 * the server.
 */

import type { FormModel, FormTemplate, KeyRow, SectionRow, TxnRow } from './types'

/** The legacy block, as an older server still emits it. */
interface LegacyAnchor {
  mode?: string
  obj_name?: string
  form_no?: string
  key_columns?: string[]
  opened_from?: string
  title_expression?: string
  one_open_instance_per_anchor?: boolean
  /** Set by the current server, which DERIVES the block from the primary. */
  derived?: boolean
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? { ...(value as Record<string, unknown>) }
    : {}
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? [...(value as T[])] : []
}

/** One transaction, with every key the editor writes into present. */
function adoptTxn(raw: unknown, index: number, keys: string[]): TxnRow {
  const t = asRecord(raw)
  const rows = asRecord(t.rows)
  const keyMap = asRecord(t.key_map)
  const kind = t.kind === 'visual' ? 'visual' : 'transaction'
  const isVisual = kind === 'visual'
  const onMissing = (['refuse', 'create', 'skip'] as const).includes(
    t.on_missing as never)
    ? (t.on_missing as TxnRow['on_missing'])
    : 'refuse'
  return {
    obj_name: String(t.obj_name || ''),
    alias: String(t.alias || ''),
    // Position decides, exactly as the server's normaliser does: whatever is
    // first IS the primary, so the two can never disagree about which.
    role: index === 0 ? 'primary' : 'linked',
    kind,
    // A visual's shape is forced, here as on the server — and forced HERE as
    // well as there because a save through a stale backend would otherwise
    // write a visual that claims to be writable, and the next load would
    // believe it. The four lines below are the same four
    // `_normalise_transactions` writes.
    form_no: isVisual ? '1' : (String(t.form_no || '1') || '1'),
    key_map: Object.fromEntries(
      keys.map((k) => [k, String(keyMap[k] ?? k)])),
    cardinality: isVisual ? 'many' : String(t.cardinality || 'one'),
    on_missing: isVisual && onMissing === 'create' ? 'skip' : onMissing,
    read_only: isVisual ? true : !!t.read_only,
    rows: {
      auto_add: isVisual
        ? false
        : (rows.auto_add === undefined ? !t.read_only : !!rows.auto_add),
      forms: asArray<string>(rows.forms).map(String),
    },
  }
}

function adoptKey(raw: unknown): KeyRow {
  const k = asRecord(raw)
  const source: KeyRow['source'] =
    k.source === 'generated' ? 'generated'
      : k.source === 'row' ? 'row'
        : 'prompt'
  return {
    key: String(k.key || ''),
    title: String(k.title || k.key || ''),
    data_type: String(k.data_type || 'VARCHAR'),
    // A generated key is allocated at save, so it cannot be mandatory at a
    // prompt that never shows it.
    // Neither a generated key nor a `row` key is ever typed: one is allocated
    // at save, the other is READ off the row. A required box for a value
    // nobody types is a box nobody can fill.
    mandatory: source === 'prompt' ? k.mandatory !== false : false,
    source,
  }
}

/**
 * One template, with the composition keys guaranteed.
 *
 * `role` defaults to `master` because that is what every template written
 * before sections existed IS — a document is opened on it. Defaulting the
 * other way would make an old form's only template unopenable.
 */
function adoptTemplate(raw: FormTemplate): FormTemplate {
  const t = raw as FormTemplate & Record<string, unknown>
  return {
    ...t,
    role: t.role === 'section' ? 'section' : 'master',
    ...(t.role === 'section'
      ? { lock_after_signoff: t.lock_after_signoff !== false } : {}),
    applies_when: String(t.applies_when || ''),
    sections: asArray(t.sections).map((s) => {
      const row = asRecord(s)
      const forEach = String(row.for_each || '')
      return {
        key: String(row.key || ''),
        title: String(row.title || ''),
        source: (row.source === 'for_each' || forEach)
          ? 'for_each' as const : 'static' as const,
        template: String(row.template || ''),
        for_each: forEach,
        template_from: String(row.template_from || ''),
        template_fallback: String(row.template_fallback || ''),
        section_no: String(row.section_no || ''),
        repeatable: row.repeatable === undefined
          ? !!forEach : !!row.repeatable,
        optional: !!row.optional,
        page_break: row.page_break === undefined ? true : !!row.page_break,
        // Carried through verbatim. This rebuild is what the designer SAVES,
        // so a key it does not list is a key opening the form takes away —
        // and `binds` is the one that gives each section of a `for_each` its
        // own record. Dropped, every step of a route wrote over one row.
        binds: (row.binds ?? null) as SectionRow['binds'],
      }
    }),
    steps: asArray(t.steps) as FormTemplate['steps'],
  }
}


export function adoptModel(raw: unknown, objName: string): FormModel {
  const model = asRecord(raw)
  const form = asRecord(model.form)
  const anchor = asRecord(form.anchor) as LegacyAnchor

  let keys = asArray(form.keys).map(adoptKey).filter((k) => k.key)
  let transactions = asArray(form.transactions)
  let open = asRecord(form.open)

  const legacy = !transactions.length && !!anchor.obj_name
  if (legacy) {
    // The same rewrite the server does (D21–D23), so an older payload edits and
    // saves in the current shape instead of being stuck in the old one.
    const columns = asArray<string>(anchor.key_columns).map(String)
    if (!keys.length) {
      keys = columns.map((c) => adoptKey({ key: c, source: 'prompt' }))
    }
    transactions = [{
      obj_name: anchor.obj_name,
      role: 'primary',
      form_no: anchor.form_no || '1',
      key_map: Object.fromEntries(columns.map((c) => [c, c])),
      // `create` made a record, `open` required one — which is exactly what
      // the two per-transaction answers mean now.
      on_missing: String(anchor.mode || 'create') === 'create'
        ? 'create' : 'refuse',
    }]
  }

  if (!Object.keys(open).length) {
    open = {
      from: String(anchor.opened_from || 'menu'),
      title_expression: String(anchor.title_expression || ''),
      one_open_instance: !!anchor.one_open_instance_per_anchor,
    }
  }

  const keyNames = keys.map((k) => k.key)
  const templates = asArray<FormTemplate>(form.templates)

  return {
    OBJ_NAME: String(model.OBJ_NAME || objName),
    OBJ_TYPE: 'E',
    form: {
      title: String(form.title || objName),
      description: String(form.description || ''),
      governance: (['none', 'audited', 'regulated'] as const).includes(
        form.governance as never)
        ? (form.governance as FormModel['form']['governance'])
        : 'none',
      keys,
      transactions: transactions.map((t, i) => adoptTxn(t, i, keyNames)),
      open,
      // Left as it arrived. The server DERIVES it from the primary on every
      // load and ignores it on the way in, so carrying it is harmless and
      // dropping it would only make a diff noisier.
      anchor: asRecord(form.anchor),
      capture: asRecord(form.capture),
      interface: asRecord(form.interface),
      // Absent on every form that has one template, which is almost all of
      // them. `null` rather than `{}` so a save carries "no selector" rather
      // than an empty one the normaliser would have to decide about.
      template_selector: (form.template_selector
        && Object.keys(asRecord(form.template_selector)).length)
        ? asRecord(form.template_selector) : null,
      // Every template gains the composition keys, defaulted. This is the
      // same guarantee the rest of `adopt` gives: a STALE BACKEND answering
      // without them must not make the Sections tab read `undefined.length`
      // on its first render.
      templates: (templates.length ? templates : [{
        key: 'MAIN', title: 'Document', revision: 1,
        body: { type: 'doc', content: [{ type: 'paragraph' }] },
        fields: [],
      }]).map(adoptTemplate),
      children: asArray(form.children),
      regulated: form.regulated ?? null,
    },
  }
}
